import React, { useRef, useState } from 'react';
import { Button, Modal, Progress, Typography, message, Space, List, Card, Tag, Divider, Tooltip } from 'antd';
import { FolderOpenOutlined, LoadingOutlined, CheckCircleOutlined, FileImageOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../utils/apiConfig';

const { Text } = Typography;

interface FolderUploadProps {
  taskId: string;
  onComplete: () => void;
  uploadType?: 'normal' | 're-solved';
  buttonLabel?: string;
  isDone?: boolean; // Add isDone prop
}

// Add type for webkitdirectory input
declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    webkitdirectory?: string;
  }
}

const FolderUploadInternal: React.FC<FolderUploadProps> = ({ taskId, onComplete, uploadType = 'normal', buttonLabel, isDone }) => {
  // State for modal lock and progress
  const [modalVisible, setModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [folderName, setFolderName] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderPath, setFolderPath] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Open folder dialog on button click
  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  // Handle folder selection
  const handleFolderSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Get folder path from the first file
    const firstFile = files[0];
    // @ts-ignore: webkitRelativePath exists
    const relativePath = firstFile.webkitRelativePath || '';
    const folder = relativePath.split('/')[0];
    
    // Extract folder path (remove the folder name from the full path)
    const fullPath = firstFile.webkitRelativePath || '';
    const pathParts = fullPath.split('/');
    pathParts.pop(); // Remove filename
    const folderPathOnly = pathParts.join('/');

    // Only JPEGs in the selected folder (ignore subfolders)
    const jpegFiles = files.filter(f => {
      // @ts-ignore: webkitRelativePath exists
      const rel = f.webkitRelativePath || '';
      // Only include files directly in the selected folder (no subfolders)
      if (rel.split('/').length !== 2) return false;
      return (
        f.type === 'image/jpeg' ||
        f.name.toLowerCase().endsWith('.jpg') ||
        f.name.toLowerCase().endsWith('.jpeg')
      );
    });

    if (jpegFiles.length === 0) {
      message.error(
        <div>
          <div><strong>No JPEG files found in the selected folder.</strong></div>
          <div>Please make sure:</div>
          <div>• The folder contains .jpg or .jpeg files</div>
          <div>• Files are in the root folder (not in subfolders)</div>
          <div>• You selected the correct folder</div>
        </div>
      );
      return;
    }

    // Store selected files and folder info for preview
    setSelectedFiles(jpegFiles);
    setFolderName(folder);
    setFolderPath(folderPathOnly);
    setTotalFiles(jpegFiles.length);
    
    // Show success message
    message.success(`Found ${jpegFiles.length} JPEG files in "${folder}". Please review and confirm upload.`);
    
    // Show preview modal instead of starting upload immediately
    setPreviewModalVisible(true);
  };

  // Handle upload confirmation from preview modal
  const handleUploadConfirm = async () => {
    if (!folderName || selectedFiles.length === 0) return;

    // --- Pre-upload check: call backend to check for duplicate triplet ---
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        taskId,
        folderName: folderName,
        uploadType: uploadType || 'normal',
      });
      const response = await fetch(`${API_ENDPOINTS.TASKS_CHECK_FOLDER_EXISTS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.exists) {
        message.warning(data.message || 'A folder with this name and upload type has already been uploaded for this task today. Please choose a different name or try again tomorrow.');
        setPreviewModalVisible(false);
        return;
      }
    } catch (err) {
      message.error('Failed to check folder uniqueness. Please try again.');
      setPreviewModalVisible(false);
      return;
    }
    // --- End pre-upload check ---

    // Close preview modal and start upload
    setPreviewModalVisible(false);
    setUploadedFiles(0);
    setErrorCount(0);
    setProgress(0);
    setModalVisible(true);
    setUploading(true);
    
    // Start upload
    await uploadFiles(selectedFiles, folderName);
  };

  // Clear selected folder
  const clearSelectedFolder = () => {
    setSelectedFiles([]);
    setFolderName(null);
    setFolderPath('');
    setTotalFiles(0);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // Upload all JPEG files, update progress
  const uploadFiles = async (jpegFiles: File[], folder: string) => {
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < jpegFiles.length; i++) {
      const file = jpegFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      // @ts-ignore
      const relativePath = file.webkitRelativePath;
      formData.append('relativePath', relativePath);
      formData.append('folderName', folder);
      if (uploadType === 're-solved') formData.append('uploadType', 're-solved');
      try {
        const response = await fetch(API_ENDPOINTS.TASKS_FOLDER_UPLOAD(taskId), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });
        if (!response.ok) throw new Error('Upload failed');
        successCount++;
        setUploadedFiles(successCount);
        setProgress(Math.round(((successCount + failCount) / jpegFiles.length) * 100));
      } catch (err) {
        failCount++;
        setErrorCount(failCount);
        setProgress(Math.round(((successCount + failCount) / jpegFiles.length) * 100));
      }
    }
    // After all uploads, call finalize
    await finalizeUpload(folder, successCount);
  };

  // Call finalize-upload API
  const finalizeUpload = async (folder: string, uploadedCount: number) => {
    try {
      const payload: any = { folderName: folder };
      if (uploadType === 're-solved') payload.uploadType = 're-solved';
      const response = await fetch(API_ENDPOINTS.TASKS_FINALIZE_UPLOAD(taskId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      setUploading(false);
      setModalVisible(false);
      if (data.success) {
        if (uploadType === 're-solved' && typeof data.reSolvedUploadCount === 'number') {
          message.success(
            <span>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              Folder re-solved uploaded successfully!<br />
              Total re-solved uploads for this task: <b>{data.reSolvedUploadCount}</b>
            </span>
          );
        } else {
          message.success(`Folder uploaded successfully! JPEGs uploaded: ${uploadedCount}`);
        }
        onComplete();
      } else {
        message.error(data.message || 'Failed to finalize upload.');
      }
    } catch (err) {
      setUploading(false);
      setModalVisible(false);
      message.error('Failed to finalize upload.');
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate total size
  const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);

  return (
    <>
      {/* Hidden input for folder selection */}
      <input
        type="file"
        ref={inputRef}
        webkitdirectory="true"
        style={{ display: 'none' }}
        onChange={handleFolderSelect}
        accept="image/jpeg,image/jpg"
      />
      <Tooltip 
        title={
          <div>
            <div><strong>How to select a folder:</strong></div>
            <div>1. Click this button</div>
            <div>2. Navigate to your folder</div>
            <div>3. Click "Select Folder" (not individual files)</div>
            <div>4. Preview will show all JPEG files found</div>
          </div>
        }
        placement="top"
      >
        <Button
          icon={<FolderOpenOutlined />}
          type={uploadType === 're-solved' ? 'default' : 'primary'}
          onClick={handleButtonClick}
          disabled={uploading || (uploadType === 'normal' && isDone)} // Disable if isDone and normal
          style={uploadType === 're-solved' ? { marginLeft: 8 } : {}}
        >
          {buttonLabel || (uploadType === 're-solved' ? 'Upload Folder Re-solved' : 'Upload Folder')}
          {selectedFiles.length > 0 && (
            <Tag color="green" style={{ marginLeft: 8, fontSize: '12px' }}>
              {selectedFiles.length} files
            </Tag>
          )}
        </Button>
      </Tooltip>

      {/* Preview Modal - Show folder contents before upload */}
      <Modal
        open={previewModalVisible}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <span>Folder Preview - Confirm Upload</span>
          </div>
        }
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPreviewModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="change" onClick={clearSelectedFolder}>
            Change Folder
          </Button>,
          <Button 
            key="upload" 
            type="primary" 
            onClick={handleUploadConfirm}
            icon={<FolderOpenOutlined />}
          >
            Start Upload ({selectedFiles.length} files)
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ marginBottom: 16 }}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>Folder Name:</Text> {folderName}
              </div>
              <Tag color={uploadType === 're-solved' ? 'orange' : 'blue'}>
                {uploadType === 're-solved' ? 'Re-solved' : 'Normal'}
              </Tag>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Path: {folderPath}</Text>
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Files: {selectedFiles.length} JPEG files • Total size: {formatFileSize(totalSize)}
              </Text>
            </div>
          </Card>
        </div>

        <Divider orientation="left">JPEG Files Found</Divider>
        
        <List
          size="small"
          dataSource={selectedFiles}
          renderItem={(file, index) => (
            <List.Item>
              <List.Item.Meta
                avatar={<FileImageOutlined style={{ color: '#52c41a' }} />}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text code>{file.name}</Text>
                    <Text type="secondary">{formatFileSize(file.size)}</Text>
                  </div>
                }
                description={
                  <Text type="secondary">
                    {file.type} • Modified: {new Date(file.lastModified).toLocaleString()}
                  </Text>
                }
              />
            </List.Item>
          )}
          style={{ maxHeight: 300, overflow: 'auto' }}
        />

        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
          <Text type="secondary">
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Only JPEG files in the root folder will be uploaded. Subfolders are ignored.
          </Text>
        </div>
      </Modal>

      {/* Upload Progress Modal */}
      <Modal
        open={modalVisible}
        closable={false}
        footer={null}
        centered
        maskClosable={false}
        keyboard={false}
        zIndex={2000}
        bodyStyle={{ textAlign: 'center', padding: 32 }}
      >
        <LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />
        <div style={{ marginTop: 16, fontSize: 18 }}>
          {uploading ? (uploadType === 're-solved' ? 'Uploading folder (Re-solved)...' : 'Uploading folder...') : 'Processing upload...'}
        </div>
        <Progress percent={progress} size="default" style={{ marginTop: 16 }} />
        <div style={{ marginTop: 16 }}>
          Files uploaded: {uploadedFiles} / {totalFiles}
          {errorCount > 0 && (
            <span style={{ color: 'red', marginLeft: 16 }}>
              Failed: {errorCount}
            </span>
          )}
        </div>
      </Modal>
    </>
  );
};

// Export two buttons for use in TaskTab or elsewhere
export const FolderUpload = (props: Omit<FolderUploadProps, 'uploadType' | 'buttonLabel'>) => (
  <Space>
    <FolderUploadInternal {...props} uploadType="normal" buttonLabel="Upload Folder Done" />
    <FolderUploadInternal {...props} uploadType="re-solved" buttonLabel="Upload Folder Fixed" />
  </Space>
); 